import React, { useState, useEffect } from 'react';
import { apiService, Dictionary } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getMaxPairsInDictionary } from '../builtInDictionaries';
import AdminConversationModal from './AdminConversationModal';
import ShareDictionaryForm from './ShareDictionaryForm';

interface User {
  id: number;
  username: string;
  email: string;
  created_at: string;
  is_verified: boolean;
  is_suspended: boolean;
  suspended_at?: string;
  dictionary_count: number;
  shares_sent: number;
  shares_received: number;
  suspension_reason?: string;
  suspended_by_username?: string;
  account_type?: string;
}

interface AdminStats {
  total_users: number;
  suspended_users: number;
  total_dictionaries: number;
  total_shares: number;
  unread_messages: number;
  new_users_30_days: number;
  public_dictionaries: number;
}

interface AdminDashboardProps {
  onClose: () => void;
  onSelectDictionary?: (dictionary: Dictionary) => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onClose, onSelectDictionary }) => {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'suspended-messages' | 'notifications' | 'my-games' | 'all-dictionaries'>('overview');
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [messages, setMessages] = useState<any[]>([]);
  const [conversationUser, setConversationUser] = useState<User | null>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedStatType, setSelectedStatType] = useState<string | null>(null);
  const [statDetails, setStatDetails] = useState<any>(null);
  const [dictionaries, setDictionaries] = useState<Dictionary[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{id: number, name: string} | null>(null);
  const [editingNameId, setEditingNameId] = useState<number | null>(null);
  const [editingNameValue, setEditingNameValue] = useState<string>('');
  const [allUsersDictionaries, setAllUsersDictionaries] = useState<any[]>([]);
  const [selectedDictUser, setSelectedDictUser] = useState<number | null>(null);
  const [showShareForm, setShowShareForm] = useState<{dictionaryId: number, dictionaryName: string} | null>(null);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      const [statsResponse, usersResponse] = await Promise.all([
        apiService.getAdminStats(),
        apiService.getAdminUsers()
      ]);
      
      setStats(statsResponse.stats);
      setUsers(usersResponse.users);
    } catch (error) {
      console.error('Failed to load admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInbox = async () => {
    try {
      const response = await apiService.getAdminInbox();
      setMessages(response.messages);
    } catch (error) {
      console.error('Failed to load admin inbox:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      // Load notifications from the notifications API
      const response = await apiService.getNotifications();
      setNotifications(response.notifications || []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      setNotifications([]);
    }
  };

  const loadDictionaries = async () => {
    try {
      const response = await apiService.getDictionaries();
      const dicts = Array.isArray(response) ? response : ((response as any).dictionaries || []);
      setDictionaries(dicts);
    } catch (err) {
      console.error('Dictionary loading error:', err);
      setDictionaries([]);
    }
  };

  const loadAllUsersDictionaries = async () => {
    try {
      const response = await apiService.getAllUsersDictionaries();
      setAllUsersDictionaries(response.dictionaries || []);
    } catch (err) {
      console.error('Failed to load all users dictionaries:', err);
      setAllUsersDictionaries([]);
    }
  };

  const saveToAdminAccount = async (dict: any) => {
    try {
      await apiService.createDictionary({
        name: `[Copy] ${dict.name}`,
        description: `Copied from user ${dict.username} on ${new Date().toLocaleDateString()}`,
        content: dict.data,
        is_public: false
      });
      await loadDictionaries();
      alert('Game saved to your account!');
    } catch (err) {
      console.error('Failed to save dictionary:', err);
      alert('Failed to save game');
    }
  };

  const handleAcceptShare = async (shareId: number) => {
    try {
      await apiService.respondToShare(shareId, 'accept');
      // Refresh notifications and dictionaries
      await loadNotifications();
      await loadDictionaries();
    } catch (error) {
      console.error('Failed to accept share:', error);
    }
  };

  const handleRejectShare = async (shareId: number) => {
    try {
      await apiService.respondToShare(shareId, 'reject');
      // Refresh notifications
      await loadNotifications();
    } catch (error) {
      console.error('Failed to reject share:', error);
    }
  };

  const deleteConversationBanner = async (userId: number) => {
    try {
      // Call API to delete conversation from backend
      await apiService.deleteAdminConversation(userId);
      // Update local state after successful API call
      setMessages(prevMessages => 
        prevMessages.filter(msg => 
          msg.sender_type === 'admin' ? 
            msg.username !== userId : 
            msg.sender_id !== userId
        )
      );
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      // Still update UI if API fails (optimistic update)
      setMessages(prevMessages => 
        prevMessages.filter(msg => 
          msg.sender_type === 'admin' ? 
            msg.username !== userId : 
            msg.sender_id !== userId
        )
      );
    }
  };

  const deleteNotification = async (notificationId: number) => {
    try {
      // Call API to delete notification
      await apiService.deleteNotification(notificationId);
      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Failed to delete notification:', error);
      // Still update UI if API fails (optimistic update)
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    }
  };

  const handleDeleteDictionary = async () => {
    if (!deleteConfirm) return;

    try {
      await apiService.deleteDictionary(deleteConfirm.id);
      setDictionaries(dicts => dicts.filter(d => d.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete dictionary:', err);
      setDeleteConfirm(null);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const startEditingName = (dictId: number, currentName: string) => {
    setEditingNameId(dictId);
    setEditingNameValue(currentName);
  };

  const cancelEditingName = () => {
    setEditingNameId(null);
    setEditingNameValue('');
  };

  const saveEditedName = async (dictId: number) => {
    if (!editingNameValue.trim()) {
      cancelEditingName();
      return;
    }

    try {
      await apiService.updateDictionary(dictId, { name: editingNameValue.trim() });
      setDictionaries(dicts =>
        dicts.map(d => d.id === dictId ? { ...d, name: editingNameValue.trim() } : d)
      );
      setEditingNameId(null);
      setEditingNameValue('');
    } catch (err) {
      console.error('Failed to update dictionary name:', err);
      cancelEditingName();
    }
  };

  const handleSuspendUser = async () => {
    if (!selectedUser || !suspensionReason.trim()) return;

    try {
      await apiService.suspendUser(selectedUser.id, suspensionReason.trim());
      await loadAdminData(); // Reload data
      setShowSuspendModal(false);
      setSuspensionReason('');
      setSelectedUser(null);
    } catch (error) {
      console.error('Failed to suspend user:', error);
    }
  };

  const handleUnsuspendUser = async (userId: number) => {
    try {
      await apiService.unsuspendUser(userId);
      await loadAdminData(); // Reload data
    } catch (error) {
      console.error('Failed to unsuspend user:', error);
    }
  };

  const handlePromoteToTeacher = async (userId: number) => {
    if (!window.confirm('Promote this user to Teacher role?')) return;

    try {
      const result = await apiService.promoteUserToTeacher(userId);
      if (result.success) {
        await loadAdminData(); // Reload data
      }
    } catch (error: any) {
      console.error('Failed to promote user:', error);
      alert(error.message || 'Failed to promote user to teacher');
    }
  };

  const handleDemoteTeacher = async (userId: number) => {
    if (!window.confirm('Demote this teacher back to regular user role?')) return;

    try {
      const result = await apiService.demoteTeacherToUser(userId);
      if (result.success) {
        await loadAdminData(); // Reload data
      }
    } catch (error: any) {
      console.error('Failed to demote teacher:', error);
      alert(error.message || 'Failed to demote teacher');
    }
  };

  const handleLogout = () => {
    logout();
    onClose(); // Close the dashboard and return to main app
  };

  const groupMessagesByUser = (messages: any[]) => {
    const grouped: { [key: string]: any } = {};
    
    messages.forEach(message => {
      // Group all messages by suspension_id to keep conversations together
      // Both user and admin messages should have the same suspension_id for the same conversation
      const conversationKey = message.suspension_id ? 
        `suspension_${message.suspension_id}` : 
        `user_${message.sender_id || message.username}`; // fallback for messages without suspension_id
      
      if (!grouped[conversationKey]) {
        // Extract user info - use suspended_user_id which is consistent for both user and admin messages
        const userId = message.suspended_user_id;
        
        grouped[conversationKey] = {
          user: {
            id: userId,
            username: message.username, // This is the suspended user's username from the backend query
            email: message.email, // This is the suspended user's email from the backend query
            suspension_reason: message.suspension_reason
          },
          messages: [],
          lastMessage: message,
          unreadCount: 0
        };
      }
      
      grouped[conversationKey].messages.push(message);
      if (!message.is_read && message.sender_type === 'user') {
        grouped[conversationKey].unreadCount++;
      }
      
      // Keep the most recent message as lastMessage
      if (new Date(message.sent_at) > new Date(grouped[conversationKey].lastMessage.sent_at)) {
        grouped[conversationKey].lastMessage = message;
      }
    });
    
    return Object.values(grouped).sort((a: any, b: any) => 
      new Date(b.lastMessage.sent_at).getTime() - new Date(a.lastMessage.sent_at).getTime()
    );
  };

  const openConversation = (user: User) => {
    setConversationUser(user);
  };

  const closeConversation = () => {
    setConversationUser(null);
  };

  const handleMessageSent = () => {
    loadInbox(); // Refresh inbox after sending message
  };

  const openStatsModal = async (statType: string) => {
    try {
      setSelectedStatType(statType);
      // TODO: Load detailed stats data from API based on statType
      // For now, using mock data
      let details = {};
      switch (statType) {
        case 'total_users':
          details = {
            title: 'Total Users',
            data: {
              verified: stats?.total_users ? Math.floor(stats.total_users * 0.8) : 0,
              unverified: stats?.total_users ? Math.floor(stats.total_users * 0.2) : 0,
              recent_registrations: stats?.new_users_30_days || 0
            }
          };
          break;
        case 'suspended_users':
          details = {
            title: 'Suspended Users',
            data: {
              total: stats?.suspended_users || 0,
              this_month: Math.floor((stats?.suspended_users || 0) * 0.3),
              pending_appeals: Math.floor((stats?.suspended_users || 0) * 0.1)
            }
          };
          break;
        case 'total_dictionaries':
          details = {
            title: 'Total Games',
            data: {
              public: stats?.public_dictionaries || 0,
              private: (stats?.total_dictionaries || 0) - (stats?.public_dictionaries || 0),
              categories: ['Languages', 'Science', 'General', 'Custom']
            }
          };
          break;
        case 'total_shares':
          details = {
            title: 'Game Shares',
            data: {
              total: stats?.total_shares || 0,
              this_week: Math.floor((stats?.total_shares || 0) * 0.1),
              most_shared_category: 'Languages'
            }
          };
          break;
        case 'unread_messages':
          details = {
            title: 'Unread Messages',
            data: {
              from_suspended: stats?.unread_messages || 0,
              urgent: Math.floor((stats?.unread_messages || 0) * 0.2),
              avg_response_time: '4.2 hours'
            }
          };
          break;
        case 'new_users_30_days':
          details = {
            title: 'New Users (30 Days)',
            data: {
              total: stats?.new_users_30_days || 0,
              daily_average: Math.floor((stats?.new_users_30_days || 0) / 30),
              growth_rate: '+12%'
            }
          };
          break;
        default:
          details = { title: 'Statistics', data: {} };
      }
      setStatDetails(details);
      setShowStatsModal(true);
    } catch (error) {
      console.error('Failed to load stat details:', error);
    }
  };

  const closeStatsModal = () => {
    setShowStatsModal(false);
    setSelectedStatType(null);
    setStatDetails(null);
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="dashboard-header">
          <h2>Admin Dashboard</h2>
          <div className="header-actions">
            <button onClick={handleLogout} className="logout-btn">🚪 Logout</button>
            <button onClick={onClose} className="close-btn">✕</button>
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h2>Admin Dashboard</h2>
        <div className="header-actions">
          <button onClick={handleLogout} className="logout-btn">🚪 Logout</button>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>
      </div>

      <div className="dashboard-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 Users ({stats?.total_users})
        </button>
        <button
          className={`tab-btn ${activeTab === 'suspended-messages' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('suspended-messages');
            loadInbox();
          }}
        >
          🚫 Suspended Messages ({stats?.unread_messages})
        </button>
        <button
          className={`tab-btn ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('notifications');
            loadNotifications();
          }}
        >
          📢 Notifications
        </button>
        <button
          className={`tab-btn ${activeTab === 'my-games' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('my-games');
            loadDictionaries();
          }}
        >
          🎮 My Games
        </button>
        <button
          className={`tab-btn ${activeTab === 'all-dictionaries' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('all-dictionaries');
            loadAllUsersDictionaries();
            setSelectedDictUser(null);
          }}
        >
          📚 All Users' Games
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'overview' && (
          <div className="admin-overview">
            <h3>System Statistics</h3>
            <div className="stats-grid">
              <div className="stat-card clickable" onClick={() => openStatsModal('total_users')}>
                <div className="stat-number">{stats?.total_users}</div>
                <div className="stat-label">Total Users</div>
                <div className="stat-hint">👁️ Click for details</div>
              </div>
              <div className="stat-card suspended clickable" onClick={() => openStatsModal('suspended_users')}>
                <div className="stat-number">{stats?.suspended_users}</div>
                <div className="stat-label">Suspended Users</div>
                <div className="stat-hint">👁️ Click for details</div>
              </div>
              <div className="stat-card clickable" onClick={() => openStatsModal('total_dictionaries')}>
                <div className="stat-number">{stats?.total_dictionaries}</div>
                <div className="stat-label">Total Games</div>
                <div className="stat-hint">👁️ Click for details</div>
              </div>
              <div className="stat-card clickable" onClick={() => openStatsModal('total_shares')}>
                <div className="stat-number">{stats?.total_shares}</div>
                <div className="stat-label">Game Shares</div>
                <div className="stat-hint">👁️ Click for details</div>
              </div>
              <div className="stat-card messages clickable" onClick={() => openStatsModal('unread_messages')}>
                <div className="stat-number">{stats?.unread_messages}</div>
                <div className="stat-label">Unread Messages</div>
                <div className="stat-hint">👁️ Click for details</div>
              </div>
              <div className="stat-card clickable" onClick={() => openStatsModal('new_users_30_days')}>
                <div className="stat-number">{stats?.new_users_30_days}</div>
                <div className="stat-label">New Users (30 days)</div>
                <div className="stat-hint">👁️ Click for details</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="admin-users">
            <div className="users-header">
              <h3>User Management</h3>
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Registered</th>
                    <th>Status</th>
                    <th>Games</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className={user.is_suspended ? 'suspended-user' : ''}>
                      <td>
                        <span className="username">{user.username}</span>
                        {!user.is_verified && <span className="unverified-badge">Unverified</span>}
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge role-${user.account_type || 'user'}`}>
                          {user.account_type === 'admin' && '👑 Admin'}
                          {user.account_type === 'teacher' && '👨‍🏫 Teacher'}
                          {(!user.account_type || user.account_type === 'user') && '👤 User'}
                        </span>
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        {user.is_suspended ? (
                          <span className="status-badge suspended">
                            Suspended
                          </span>
                        ) : (
                          <span className="status-badge active">Active</span>
                        )}
                      </td>
                      <td>
                        <span className="dict-count">{user.dictionary_count}</span>
                        <small> ({user.shares_sent} sent, {user.shares_received} received)</small>
                      </td>
                      <td>
                        <div className="user-actions">
                          <button
                            onClick={() => setSelectedUser(user)}
                            className="action-btn view-btn"
                            title="View Details"
                          >
                            👁️
                          </button>
                          {user.account_type !== 'admin' && user.account_type !== 'teacher' && (
                            <button
                              onClick={() => handlePromoteToTeacher(user.id)}
                              className="action-btn promote-btn"
                              title="Promote to Teacher"
                            >
                              👨‍🏫
                            </button>
                          )}
                          {user.account_type === 'teacher' && (
                            <button
                              onClick={() => handleDemoteTeacher(user.id)}
                              className="action-btn demote-btn"
                              title="Demote to User"
                            >
                              ⬇️
                            </button>
                          )}
                          {user.is_suspended ? (
                            <button
                              onClick={() => handleUnsuspendUser(user.id)}
                              className="action-btn unsuspend-btn"
                              title="Unsuspend User"
                            >
                              ✅
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowSuspendModal(true);
                              }}
                              className="action-btn suspend-btn"
                              title="Suspend User"
                            >
                              🚫
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'suspended-messages' && (
          <div className="admin-inbox">
            <h3>🚫 Suspended User Conversations</h3>
            <div className="conversations-container">
              {messages.length === 0 ? (
                <div className="no-conversations">
                  <p>📬 No messages from suspended users yet.</p>
                  <small>Suspended users can send up to 3 messages per day to communicate with admin.</small>
                </div>
              ) : (
                groupMessagesByUser(messages).map((conversation: any) => (
                  <div 
                    key={conversation.user.id} 
                    className={`conversation-banner ${conversation.unreadCount > 0 ? 'has-unread' : ''}`}
                  >
                    <div className="conversation-header">
                      <div className="user-info">
                        <span className="username">🚫 {conversation.user.username}</span>
                        <span className="user-email">{conversation.user.email}</span>
                      </div>
                      <div className="conversation-meta">
                        {conversation.unreadCount > 0 && (
                          <span className="unread-badge">{conversation.unreadCount}</span>
                        )}
                        <span className="last-message-time">
                          {formatDate(conversation.lastMessage.sent_at)}
                        </span>
                        <button 
                          className="delete-conversation-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteConversationBanner(conversation.user.id);
                          }}
                          title="Delete conversation"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <div className="last-message-preview" onClick={() => openConversation(conversation.user)}>
                      <span className="message-sender">
                        {conversation.lastMessage.sender_type === 'admin' ? 'You' : conversation.user.username}:
                      </span>
                      <span className="message-preview">
                        {conversation.lastMessage.message.length > 100 
                          ? conversation.lastMessage.message.substring(0, 100) + '...'
                          : conversation.lastMessage.message
                        }
                      </span>
                    </div>
                    {conversation.user.suspension_reason && (
                      <div className="suspension-info">
                        <small><strong>Suspended for:</strong> {conversation.user.suspension_reason}</small>
                      </div>
                    )}
                    <div className="conversation-action" onClick={() => openConversation(conversation.user)}>
                      <span className="click-hint">💬 Click to open conversation</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="admin-notifications">
            <h3>📢 General Notifications</h3>
            <div className="notifications-container">
              {notifications.length === 0 ? (
                <div className="no-notifications">
                  <p>🔔 No general notifications yet.</p>
                  <small>Game shares, user registrations, and other notifications will appear here.</small>
                </div>
              ) : (
                notifications.map((notification: any) => (
                  <div key={notification.id} className="notification-banner">
                    <div className="notification-header">
                      <div className="notification-type">
                        <span className="type-icon">
                          {notification.type === 'dictionary_share_received' ? '📚' : '📬'}
                        </span>
                        <span className="type-label">
                          {notification.type === 'dictionary_share_received' ? 'Game Share' : 'Notification'}
                        </span>
                      </div>
                      <div className="notification-meta">
                        <span className="notification-time">
                          {formatDate(notification.created_at)}
                        </span>
                        <button
                          className="delete-notification-btn"
                          onClick={() => deleteNotification(notification.id)}
                          title="Delete notification"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <div className="notification-content">
                      <p>{notification.message}</p>
                      {notification.from_username && (
                        <small>From: {notification.from_username}</small>
                      )}
                    </div>
                    {notification.type === 'dictionary_share_received' && notification.related_id && (
                      <div className="notification-actions">
                        <button
                          className="accept-btn"
                          onClick={() => handleAcceptShare(notification.related_id)}
                        >
                          ✅ Accept
                        </button>
                        <button
                          className="reject-btn"
                          onClick={() => handleRejectShare(notification.related_id)}
                        >
                          ❌ Reject
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'my-games' && (
          <div className="admin-my-games">
            <h3>🎮 My Saved Games ({dictionaries.length})</h3>
            {dictionaries.length === 0 ? (
              <div className="no-games">
                <p>You haven't saved any games yet.</p>
                <p>Play a game and save it to see it here!</p>
              </div>
            ) : (
              <div className="dictionaries-grid">
                {dictionaries.map(dict => (
                  <div key={dict.id} className="dictionary-card">
                    <div className="dictionary-header">
                      {editingNameId === dict.id ? (
                        <div className="name-edit-container">
                          <input
                            type="text"
                            className="name-edit-input"
                            value={editingNameValue}
                            onChange={(e) => setEditingNameValue(e.target.value)}
                            onBlur={() => saveEditedName(dict.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                saveEditedName(dict.id);
                              }
                              if (e.key === 'Escape') {
                                cancelEditingName();
                              }
                            }}
                            autoFocus
                            maxLength={100}
                          />
                        </div>
                      ) : (
                        <div className="name-display-container">
                          <h4>{dict.name}</h4>
                          <button
                            className="edit-name-btn"
                            onClick={() => startEditingName(dict.id, dict.name)}
                            title="Edit game name"
                          >
                            ✏️
                          </button>
                        </div>
                      )}
                      <span className="dictionary-visibility">
                        {dict.is_public ? '🌐 Public' : '🔒 Private'}
                      </span>
                    </div>
                    <div className="dictionary-info">
                      <p>{getMaxPairsInDictionary(dict.data)} word matches</p>
                      <p className="dictionary-date">Created: {formatDate(dict.created_at)}</p>
                      {dict.description && <p className="dictionary-description">{dict.description}</p>}
                    </div>
                    <div className="dictionary-actions">
                      <button
                        onClick={() => onSelectDictionary?.(dict)}
                        className="play-button"
                      >
                        Play Game
                      </button>
                      {!dict.is_public && (
                        <button
                          onClick={() => setShowShareForm({dictionaryId: dict.id, dictionaryName: dict.name})}
                          className="share-button"
                          style={{
                            backgroundColor: '#2196F3',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            margin: '0 4px'
                          }}
                        >
                          📤 Share
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteConfirm({id: dict.id, name: dict.name})}
                        className="delete-button"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'all-dictionaries' && (
          <div className="admin-all-dictionaries">
            <h3>📚 All Users' Game Dictionaries</h3>

            {selectedDictUser === null ? (
              // Show list of users grouped by user
              <div className="users-dict-list">
                {allUsersDictionaries.length === 0 ? (
                  <div className="no-data">
                    <p>No user dictionaries found.</p>
                  </div>
                ) : (
                  <div className="users-grid">
                    {Object.entries(
                      allUsersDictionaries.reduce((acc: any, dict: any) => {
                        if (!acc[dict.user_id]) {
                          acc[dict.user_id] = {
                            userId: dict.user_id,
                            username: dict.username,
                            count: 0,
                            dictionaries: []
                          };
                        }
                        acc[dict.user_id].count++;
                        acc[dict.user_id].dictionaries.push(dict);
                        return acc;
                      }, {})
                    ).map(([userId, userData]: [string, any]) => (
                      <div
                        key={userId}
                        className="user-dict-card"
                        onClick={() => setSelectedDictUser(userData.userId)}
                      >
                        <div className="user-dict-header">
                          <h4>👤 {userData.username}</h4>
                          <span className="dict-count">{userData.count} games</span>
                        </div>
                        <div className="user-dict-preview">
                          <p>Click to view all games</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Show specific user's dictionaries
              <div className="user-dictionaries-view">
                <button
                  className="back-to-users-btn"
                  onClick={() => setSelectedDictUser(null)}
                >
                  ← Back to Users List
                </button>

                {(() => {
                  const userDicts = allUsersDictionaries.filter(d => d.user_id === selectedDictUser);
                  const username = userDicts[0]?.username || 'User';

                  return (
                    <>
                      <h4>Games by {username} ({userDicts.length})</h4>
                      <div className="dictionaries-grid">
                        {userDicts.map(dict => (
                          <div key={dict.id} className="dictionary-card">
                            <div className="dictionary-header">
                              <h4>{dict.name}</h4>
                              <span className="dictionary-visibility">
                                {dict.is_public ? '🌐 Public' : '🔒 Private'}
                              </span>
                            </div>
                            <div className="dictionary-info">
                              <p>{getMaxPairsInDictionary(dict.data)} word matches</p>
                              <p className="dictionary-date">Created: {formatDate(dict.created_at)}</p>
                              {dict.description && <p className="dictionary-description">{dict.description}</p>}
                            </div>
                            <div className="dictionary-actions">
                              <button
                                onClick={() => onSelectDictionary?.({ ...dict, id: dict.id, name: dict.name, data: dict.data, is_public: dict.is_public, created_at: dict.created_at })}
                                className="play-button"
                              >
                                🎮 Play
                              </button>
                              <button
                                onClick={() => saveToAdminAccount(dict)}
                                className="save-button"
                                style={{
                                  backgroundColor: '#9C27B0',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  margin: '0 4px'
                                }}
                              >
                                💾 Save to My Account
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Suspension Modal */}
      {showSuspendModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-content">
              <h3>Suspend User: {selectedUser.username}</h3>
              <div className="suspension-form">
                <label htmlFor="suspension-reason">Reason for suspension:</label>
                <textarea
                  id="suspension-reason"
                  value={suspensionReason}
                  onChange={(e) => setSuspensionReason(e.target.value)}
                  placeholder="Please provide a detailed reason for the suspension..."
                  rows={4}
                  className="suspension-textarea"
                />
                <div className="modal-actions">
                  <button
                    onClick={() => {
                      setShowSuspendModal(false);
                      setSuspensionReason('');
                    }}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSuspendUser}
                    disabled={!suspensionReason.trim()}
                    className="suspend-btn"
                  >
                    Suspend User
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {selectedUser && !showSuspendModal && (
        <div className="modal-overlay">
          <div className="modal-container large">
            <div className="modal-content">
              <div className="user-details-header">
                <h3>User Details: {selectedUser.username}</h3>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="close-btn"
                >
                  ✕
                </button>
              </div>
              <div className="user-details-content">
                <div className="user-info">
                  <p><strong>Email:</strong> {selectedUser.email}</p>
                  <p><strong>Registered:</strong> {formatDate(selectedUser.created_at)}</p>
                  <p><strong>Verified:</strong> {selectedUser.is_verified ? '✅' : '❌'}</p>
                  <p><strong>Status:</strong> {selectedUser.is_suspended ? '🚫 Suspended' : '✅ Active'}</p>
                  {selectedUser.is_suspended && selectedUser.suspension_reason && (
                    <p><strong>Suspension Reason:</strong> {selectedUser.suspension_reason}</p>
                  )}
                </div>
                <div className="user-stats">
                  <p><strong>Games:</strong> {selectedUser.dictionary_count}</p>
                  <p><strong>Shares Sent:</strong> {selectedUser.shares_sent}</p>
                  <p><strong>Shares Received:</strong> {selectedUser.shares_received}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Conversation Modal */}
      {conversationUser && (
        <AdminConversationModal
          user={conversationUser}
          onClose={closeConversation}
          onMessageSent={handleMessageSent}
        />
      )}

      {/* Statistics Details Modal */}
      {showStatsModal && statDetails && (
        <div className="modal-overlay">
          <div className="modal-container stats-modal">
            <div className="modal-content">
              <div className="stats-modal-header">
                <h3>{statDetails.title}</h3>
                <button onClick={closeStatsModal} className="close-btn">✕</button>
              </div>
              <div className="stats-modal-content">
                {selectedStatType === 'total_users' && (
                  <div className="stats-details">
                    <div className="detail-item">
                      <span className="detail-label">Verified Users:</span>
                      <span className="detail-value">{statDetails.data.verified}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Unverified Users:</span>
                      <span className="detail-value">{statDetails.data.unverified}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Recent Registrations:</span>
                      <span className="detail-value">{statDetails.data.recent_registrations}</span>
                    </div>
                  </div>
                )}
                {selectedStatType === 'suspended_users' && (
                  <div className="stats-details">
                    <div className="detail-item">
                      <span className="detail-label">Total Suspended:</span>
                      <span className="detail-value">{statDetails.data.total}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Suspended This Month:</span>
                      <span className="detail-value">{statDetails.data.this_month}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Pending Appeals:</span>
                      <span className="detail-value">{statDetails.data.pending_appeals}</span>
                    </div>
                  </div>
                )}
                {selectedStatType === 'total_dictionaries' && (
                  <div className="stats-details">
                    <div className="detail-item">
                      <span className="detail-label">Public Games:</span>
                      <span className="detail-value">{statDetails.data.public}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Private Games:</span>
                      <span className="detail-value">{statDetails.data.private}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Popular Categories:</span>
                      <span className="detail-value">{statDetails.data.categories.join(', ')}</span>
                    </div>
                  </div>
                )}
                {selectedStatType === 'total_shares' && (
                  <div className="stats-details">
                    <div className="detail-item">
                      <span className="detail-label">Total Shares:</span>
                      <span className="detail-value">{statDetails.data.total}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Shares This Week:</span>
                      <span className="detail-value">{statDetails.data.this_week}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Most Shared Category:</span>
                      <span className="detail-value">{statDetails.data.most_shared_category}</span>
                    </div>
                  </div>
                )}
                {selectedStatType === 'unread_messages' && (
                  <div className="stats-details">
                    <div className="detail-item">
                      <span className="detail-label">From Suspended Users:</span>
                      <span className="detail-value">{statDetails.data.from_suspended}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Urgent Messages:</span>
                      <span className="detail-value">{statDetails.data.urgent}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Avg Response Time:</span>
                      <span className="detail-value">{statDetails.data.avg_response_time}</span>
                    </div>
                  </div>
                )}
                {selectedStatType === 'new_users_30_days' && (
                  <div className="stats-details">
                    <div className="detail-item">
                      <span className="detail-label">New Users (30 days):</span>
                      <span className="detail-value">{statDetails.data.total}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Daily Average:</span>
                      <span className="detail-value">{statDetails.data.daily_average}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Growth Rate:</span>
                      <span className="detail-value">{statDetails.data.growth_rate}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dictionary Confirmation Modal */}
      {deleteConfirm && (
        <div className="confirm-popup-overlay">
          <div className="confirm-popup">
            <h3>Delete Game</h3>
            <p>Are you sure you want to delete "<strong>{deleteConfirm.name}</strong>"?</p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="confirm-popup-actions">
              <button className="cancel-btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="delete-btn" onClick={handleDeleteDictionary}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Share Dictionary Modal */}
      {showShareForm && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-content">
              <ShareDictionaryForm
                dictionaryId={showShareForm.dictionaryId}
                dictionaryName={showShareForm.dictionaryName}
                onSuccess={() => setShowShareForm(null)}
                onCancel={() => setShowShareForm(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;